import { Component, EventEmitter, OnInit, Output, computed, effect, inject, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../../core/services/api.service';
import { CrmProductRow } from '../../deck-admin.models';
import { PaginationState } from '../../deck-list-state';
import { productStamp, resolveProductRow, toProductContext } from '../../selected-product.util';
import { PaginationComponent } from '../../../../../shared/components/pagination/pagination.component';
import { FOR_ENV_OPTIONS, envIncludesProduction, forEnvBadgeClass, forEnvLabel } from '../../for-env.util';
import { AgentPromptService } from './agent-prompt.service';
import { AgentPrompt } from './agent-prompt.models';
import {
  PromptFormPanelComponent,
  PromptPanelMode,
} from './components/prompt-form-panel/prompt-form-panel.component';

/**
 * Agent Prompts tab: a table of the selected product's named live-call prompts.
 *
 * Product scope mirrors the deck board — the tab reacts to the CRM's global product
 * selection, resolves it, and points {@link AgentPromptService} at that product.
 * Backend is built after this frontend; until then the table shows an error/empty
 * state cleanly (see the service's documented contract).
 */
@Component({
  selector: 'app-agent-prompt',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, PromptFormPanelComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './agent-prompt.component.html',
})
export class AgentPromptComponent implements OnInit {
  private promptService = inject(AgentPromptService);
  private apiService = inject(ApiService);

  /** Emits the prompt count after each load so the tab shell can badge it. */
  @Output() countChange = new EventEmitter<number>();

  prompts = signal<AgentPrompt[]>([]);
  loading = signal(false);
  errorMsg = signal('');
  includeInactive = signal(true);

  // Panel
  panelOpen = signal(false);
  panelMode = signal<PromptPanelMode>('create');
  selectedPrompt = signal<AgentPrompt | null>(null);
  /** Tab the panel opens on — the row's History shortcut jumps straight there. */
  panelInitialTab = signal<'editor' | 'history'>('editor');

  // Delete
  deleteTarget = signal<AgentPrompt | null>(null);
  deleting = signal(false);

  // Environment move (Test / Production / Both), pending the confirm dialog.
  envChangeTarget = signal<{ prompt: AgentPrompt; to: number } | null>(null);
  changingEnv = signal(false);

  // Env selector helpers for the template.
  readonly envOptions = FOR_ENV_OPTIONS;
  readonly envLabel = forEnvLabel;
  readonly envBadgeClass = forEnvBadgeClass;
  readonly envIncludesProduction = envIncludesProduction;

  readonly pager = new PaginationState(10);
  paginatedPrompts = computed(() => this.pager.slice(this.prompts()));

  /** Flips once /product/list-product has answered (or failed). */
  private productsReady = signal(false);
  private appliedProductStamp: string | undefined;

  activeProduct = computed<CrmProductRow | null>(() =>
    resolveProductRow(
      (this.apiService.products() ?? []) as CrmProductRow[],
      this.apiService.selectedProductId()
    )
  );

  constructor() {
    effect(() => {
      if (!this.productsReady()) return;
      const product = this.activeProduct();
      untracked(() => this.applyProductScope(product));
    });
  }

  ngOnInit(): void {
    this.apiService.fetchProducts().subscribe({
      next: () => this.productsReady.set(true),
      error: () => this.productsReady.set(true),
    });
  }

  /** Point the service at `product`'s prompts and reload. No-op if scope is unchanged. */
  private applyProductScope(product: CrmProductRow | null): void {
    const stamp = productStamp(product);
    if (stamp === this.appliedProductStamp) return;
    this.appliedProductStamp = stamp;
    if (!product) {
      this.errorMsg.set('No products available — agent prompts need a product to manage.');
      return;
    }
    this.promptService.setProductContext(toProductContext(product));
    this.pager.reset();
    this.loadPrompts();
  }

  loadPrompts(): void {
    this.loading.set(true);
    this.errorMsg.set('');
    this.promptService.listPrompts(this.includeInactive()).subscribe({
      next: (res) => {
        this.prompts.set(res.prompts ?? []);
        this.countChange.emit(res.count ?? res.prompts?.length ?? 0);
        this.loading.set(false);
      },
      error: (err) => {
        this.prompts.set([]);
        this.countChange.emit(0);
        this.errorMsg.set(err?.message ?? 'Failed to load prompts.');
        this.loading.set(false);
      },
    });
  }

  toggleIncludeInactive(): void {
    this.includeInactive.set(!this.includeInactive());
    this.loadPrompts();
  }

  // ── panel ──────────────────────────────────────────────────────────────────

  openPanel(mode: PromptPanelMode, prompt: AgentPrompt | null, tab: 'editor' | 'history' = 'editor'): void {
    this.panelMode.set(mode);
    this.selectedPrompt.set(prompt);
    this.panelInitialTab.set(tab);
    this.panelOpen.set(true);
  }

  closePanel(): void {
    this.panelOpen.set(false);
    this.selectedPrompt.set(null);
  }

  onSaved(): void {
    this.closePanel();
    this.loadPrompts();
  }

  // ── writes ─────────────────────────────────────────────────────────────────

  toggleActive(prompt: AgentPrompt): void {
    this.promptService.patchPrompt(prompt.id, { is_active: !prompt.isActive }).subscribe({
      next: () => this.loadPrompts(),
      error: (err) => this.errorMsg.set(err?.message ?? 'Could not update active status.'),
    });
  }

  /** Row env selector changed — hold the move until the user confirms it. */
  requestEnvChange(prompt: AgentPrompt, to: number | string): void {
    const target = Number(to);
    if (!target || target === prompt.forEnv) return;
    this.envChangeTarget.set({ prompt, to: target });
  }

  /** Reload so the row's selector snaps back to the unchanged value. */
  cancelEnvChange(): void {
    this.envChangeTarget.set(null);
    this.loadPrompts();
  }

  executeEnvChange(): void {
    const change = this.envChangeTarget();
    if (!change) return;
    this.changingEnv.set(true);
    this.promptService.patchPrompt(change.prompt.id, { for_env: change.to }).subscribe({
      next: () => {
        this.changingEnv.set(false);
        this.envChangeTarget.set(null);
        this.loadPrompts();
      },
      error: (err) => {
        this.changingEnv.set(false);
        this.envChangeTarget.set(null);
        this.errorMsg.set(err?.message ?? 'Environment change failed.');
        this.loadPrompts();
      },
    });
  }

  confirmDelete(prompt: AgentPrompt): void {
    this.deleteTarget.set(prompt);
  }

  cancelDelete(): void {
    this.deleteTarget.set(null);
  }

  executeDelete(): void {
    const target = this.deleteTarget();
    if (!target) return;
    this.deleting.set(true);
    this.promptService.deletePrompt(target.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.cancelDelete();
        this.loadPrompts();
      },
      error: (err) => {
        this.deleting.set(false);
        this.cancelDelete();
        this.errorMsg.set(err?.message ?? 'Delete failed.');
      },
    });
  }

  trackByPrompt(_: number, prompt: AgentPrompt): number {
    return prompt.id;
  }
}
